import useSessionStore from '../state/session';
import React from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  SECURITY_LEVEL_RANK,
  type AccessScope,
  type FeaturePermissionLevel,
  type PagePermissionLevel,
  type SecurityLevel,
} from '../types/security';

export function useRoles(){
  const securityLevel = useSessionStore(s=>s.securityLevel);
  const accessRoleCodes = useSessionStore(s=>s.accessRoleCodes);
  const accessConfigured = useSessionStore(s=>s.accessConfigured);
  const isAccessPreview = useSessionStore(s=>s.isAccessPreview);
  const session = useSessionStore(s=>s.session);
  const basePagePermissions = useSessionStore(s=>s.pagePermissions);
  const pageScopes = useSessionStore(s=>s.pageScopes);
  const baseFeaturePermissions = useSessionStore(s=>s.featurePermissions);
  const featureScopes = useSessionStore(s=>s.featureScopes);
  const profile = useSessionStore(s=>s.profile);
  const isAuthenticated = !!session;
  const roles = React.useMemo(() => isAuthenticated ? accessRoleCodes : [], [accessRoleCodes, isAuthenticated]);
  const has = (r:string)=> roles.includes(r);
  const atLeast = React.useCallback((minimum: SecurityLevel) => {
    if (!isAuthenticated || !securityLevel) return false;
    return SECURITY_LEVEL_RANK[securityLevel] >= SECURITY_LEVEL_RANK[minimum];
  }, [isAuthenticated, securityLevel]);
  const isSuperAdmin = isAuthenticated && !isAccessPreview && securityLevel === 'super_admin';
  const isAdmin = !isAccessPreview && atLeast('admin');
  const isManager = !isAccessPreview && atLeast('manager');
  const isUser = atLeast('user');
  const isImpersonating = isAuthenticated && isAccessPreview;
  const effectiveSuperAdmin = isSuperAdmin;
  const isArchived = isAuthenticated && profile?.is_archived === true;

  const isFreelancer = isAuthenticated && securityLevel === 'guest' && has('freelancer');
  const isAccounting = isAuthenticated && has('accounting');
  const isService = isAuthenticated && (has('svc') || has('service'));

  const getPagePermission = React.useCallback(
    (pageKey: string): PagePermissionLevel => {
      return (basePagePermissions && basePagePermissions[pageKey]) || 'none';
    },
    [basePagePermissions],
  );

  const canViewPage = React.useCallback(
    (pageKey: string) => {
      const level = getPagePermission(pageKey);
      return level === 'view' || level === 'edit';
    },
    [getPagePermission],
  );

  const canEditPage = React.useCallback(
    (pageKey: string) => !isAccessPreview && getPagePermission(pageKey) === 'edit',
    [getPagePermission, isAccessPreview],
  );

  const getFeaturePermission = React.useCallback(
    (featureKey: string): FeaturePermissionLevel => {
      return (baseFeaturePermissions && baseFeaturePermissions[featureKey]) || 'none';
    },
    [baseFeaturePermissions],
  );

  const getPageScope = React.useCallback(
    (pageKey: string): AccessScope => pageScopes[pageKey] || 'self',
    [pageScopes],
  );

  const getFeatureScope = React.useCallback(
    (featureKey: string): AccessScope => featureScopes[featureKey] || 'self',
    [featureScopes],
  );

  const canViewFeature = React.useCallback(
    (featureKey: string) => getFeaturePermission(featureKey) !== 'none',
    [getFeaturePermission],
  );

  const canEditFeature = React.useCallback(
    (featureKey: string, scope: 'own' | 'all' = 'own') => {
      if (isAccessPreview) return false;
      const permission = getFeaturePermission(featureKey);
      if (permission === 'edit_all') return true;
      return scope === 'own' && permission === 'edit_own';
    },
    [getFeaturePermission, isAccessPreview],
  );

  // Shared capabilities now use the same permission matrix as navigation and
  // direct routes. Existing role checks remain where the database enforces an
  // additional administrative or accounting boundary.
  const canViewVenues = canViewPage('venues');
  const canViewEvents = canViewPage('events');
  const canEditVenues = canEditPage('venues');
  const canDeleteVenues = canEditVenues && (isManager || isAdmin);
  const canEditEvent = canEditPage('events');
  const canEditRooms = canEditEvent;
  const canEditVenueRooms = canEditVenues;
  const canEditContacts = canEditEvent;
  const canEditAgenda = canEditEvent;
  const canEditGuidelines = canEditEvent;
  const canUploadPhotos = canEditEvent || canEditVenues;
  const canEditPhotosMeta = canUploadPhotos;
  const canViewHistory = canViewPage('history');
  const canUndoHistory = canEditPage('history') && (isManager || isAdmin);
  const canInvite = canEditFeature('maintenance.users', 'own') || canEditPage('users');
  const canManagePermissions = canEditPage('security');
  const canAssignRoles = isAdmin && canEditPage('security');
  const canAccessService = canViewPage('service');
  const canUnlockLockedAsset = (
    (isAdmin || isAccounting)
    && (canEditPage('inventory') || canEditPage('admin_inventory'))
  );
  const canViewVenueContracts = canViewPage('venue_contracts');
  const canEditVenueContracts = canEditPage('venue_contracts');
  // Invoice and master-bill records need the Invoicing page and the accounting boundary the
  // database enforces (invoice_accounting_actor_allowed: admins and accounting); managers are refused.
  const canOpenInvoicing = canViewPage('invoicing') && (isAdmin || isAccounting);

  return {
    securityLevel,
    accessRoleCodes: roles,
    accessConfigured,
    atLeast,
    roles: isAuthenticated ? roles : [],
    isArchived,
    isUser,
    isManager,
    isAdmin,
    isSuperAdmin,
    isImpersonating,
    effectiveSuperAdmin,
    pagePermissions: basePagePermissions,
    pageScopes,
    featurePermissions: baseFeaturePermissions,
    featureScopes,
    getPagePermission,
    getPageScope,
    canViewPage,
    canEditPage,
    getFeaturePermission,
    getFeatureScope,
    canViewFeature,
    canEditFeature,
    // view permissions
    canViewVenues,
    canViewEvents,
    canViewHistory,
    // edit permissions
    canEditEvent,
    canEditVenues,
    canDeleteVenues,
    canEditRooms,
    canEditVenueRooms,
    canEditContacts,
    canEditAgenda,
    canEditGuidelines,
    canUploadPhotos,
    canEditPhotosMeta,
    // higher-level actions
    canUndoHistory,
    canInvite,
    canManagePermissions,
    canAssignRoles,
    isAccounting,
    isService,
    canAccessService,
    canUnlockLockedAsset,
    canViewVenueContracts,
    canEditVenueContracts,
    canOpenInvoicing,
    isFreelancer,
  };
}

// Persist impersonation across reload
export function usePersistImpersonation(supabase: SupabaseClient, enabled = true){
  const impersonated = useSessionStore(s=>s.impersonatedRoles);
  const setImp = useSessionStore(s=>s.setImpersonatedRoles!);
  const session = useSessionStore(s=>s.session);
  const hydratedPrincipalRef = React.useRef<string | null>(null);
  const syncChainRef = React.useRef<Promise<void>>(Promise.resolve());

  // On mount, hydrate from server-side impersonation if present
  React.useEffect(()=>{
    let cancelled = false;
    hydratedPrincipalRef.current = null;
    (async () => {
      try {
        if (!enabled) { setImp(null); return; }
        if(!session?.user?.id){ setImp(null); return; }
        const { data, error } = await supabase.rpc('current_impersonated_role');
        if (!cancelled && !error) {
          hydratedPrincipalRef.current = session.user.id;
          if (typeof data === 'string' && data) setImp([data]);
          else setImp(null);
        }
      } catch(_e){}
    })();
    return () => { cancelled = true; };
  }, [enabled, session?.user?.id, supabase, setImp]);

  // When impersonated changes, sync to server RPCs. One call at a time, so ending a
  // preview never lands before an extension still in flight, and a superseded change
  // is skipped.
  React.useEffect(()=>{
    let cancelled = false;
    syncChainRef.current = syncChainRef.current.then(async () => {
      try {
        if (cancelled || !enabled) return;
        if(!session?.user?.id) return;
        if (hydratedPrincipalRef.current !== session.user.id) return;
        let error: unknown = null;
        if (impersonated && impersonated.length) {
          // Extend/create a server-side read-only access preview.
          ({ error } = await supabase.rpc('impersonate_as_role', { role: impersonated[0], ttl_minutes: 30 }));
        } else {
          ({ error } = await supabase.rpc('clear_impersonation'));
        }
        if (error) throw error;
        // A load already in flight may have read the preview before this change.
        if (!cancelled) await useSessionStore.getState().loadContext(supabase, { fresh: true });
      } catch(_e){
        // The next context refresh remains fail-closed; do not invent a local
        // preview that the authoritative database did not accept.
        if (!cancelled) {
          hydratedPrincipalRef.current = null;
          setImp(null);
        }
      }
    });
    return () => { cancelled = true; };
  }, [enabled, impersonated, session?.user?.id, setImp, supabase]);
}
